<template>
  <DeviceSetupSection>
    <template v-slot:left>
      <PageContainer
        :class="$style.container"
      >
        <template v-slot:body>
          <Container>
            <LoadingOverlay
              :hasBlur="true"
              :isLight="true"
              :visible="isLoading"
            />
            <HeadingLarge>
              {{ selectedInstall.header }}
            </HeadingLarge>
            <div v-if="!onLastStep">
              <div v-if="isApnsPushTopicEmpty">
                <ParagraphBody>
                  You haven't configured MDM for your organization yet.
                </ParagraphBody>
                <ParagraphBody>
                  Let's go over to MDM to get started.
                </ParagraphBody>
              </div>
              <div v-else>
                <ParagraphBody>
                  Go ahead and download the MDM Enrollment Profile now.
                </ParagraphBody>
                <IconButton
                  :class="$style.downloadBtn"
                  icon="fileDownload"
                  :outline="true"
                  text="Download MDM Enrollment Profile"
                  type="secondary"
                  @click="downloadProfile"
                />
                <CompatibilityNotes
                  :compatibilityNotes="compatibilityNotes"
                />
              </div>
            </div>
            <DeviceSetupSuccess
              v-else
              subheader="You can fill out more details about this device later."
            />
          </Container>
        </template>

        <template v-slot:footer>
          <Container>
            <ButtonGroupSave
              :isFullWidth="false"
              :primaryButtonProps="primaryButtonProps"
              :secondaryButtonProps="secondaryButtonProps"
              @primaryAction="primaryAction"
              @secondaryAction="secondaryAction"
            />
          </Container>
        </template>
      </PageContainer>
    </template>
    <template v-slot:right>
      <div
        :class="[$style.image, {
          [$style.center]: currentStep === lastStep,
        }]"
        :style="{ backgroundImage: image }"
      />
    </template>
  </DeviceSetupSection>
</template>

<script>
import {
  ButtonGroupSave,
  Container,
  HeadingLarge,
  IconButton,
  LoadingOverlay,
  PageContainer,
  ParagraphBody,
  Types,
} from '@jumpcloud/ui-components';
import { SystemCompatibilityConsts } from 'client/util/Constants';
import { eventLocations, eventProperties } from 'client/GrowthEngineering/utilities/GrowthConstants';
import CompatibilityNotes from 'client/systems/CompatibilityNotes.vue';
import DeviceSetupSection from 'client/Discover/components/GuidedSetup/DeviceSetupSection.vue';
import DeviceSetupSuccess from 'client/Discover/components/GuidedSetup/DeviceSetupSuccess.vue';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import GuidedSetupMixin from 'client/Discover/components/GuidedSetup/mixins/GuidedSetupMixin';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import MdmSettingsMixin from 'client/MDM/mixins/MdmSettingsMixin';
import NavPanelLinkList from 'client/components/NavPanel/NavPanelLinkList';
import Util from 'client/util/Util';

const { PrimaryButtonProps } = Types;
const { mdmLink } = NavPanelLinkList;
const {
  description,
  displayLogic,
  location,
  section,
  text,
  toUrl,
} = eventProperties;

export default {
  name: 'MdmSetup',

  components: {
    ButtonGroupSave,
    CompatibilityNotes,
    Container,
    DeviceSetupSection,
    DeviceSetupSuccess,
    HeadingLarge,
    IconButton,
    LoadingOverlay,
    PageContainer,
    ParagraphBody,
  },

  mixins: [
    GuidedSetupMixin,
    MdmSettingsMixin,
  ],

  EventHelper,
  Util,

  data: () => ({
    compatibilityNotes: {
      header: 'Supported Operating Systems',
      kbLink: {
        href: KbArticleUrls.mdmEnrollmentPolicy,
        text: 'Learn more',
      },
      text: SystemCompatibilityConsts.mac,
    },
    complete: false,
    currentStep: 1,
    isLoading: false,
    lastStep: 2,
  }),

  computed: {
    image() {
      return this.isApnsPushTopicEmpty ? this.selectedInstall.images.default : this.backgroundImage;
    },

    primaryButtonProps() {
      let buttonText;

      if (this.isApnsPushTopicEmpty) {
        buttonText = 'Configure MDM';
      } else if (this.onLastStep) {
        buttonText = 'Done';
      } else {
        buttonText = 'Next';
      }

      return PrimaryButtonProps({ text: buttonText });
    },
  },

  mounted() {
    this.handleMounted();
  },

  methods: {
    configureMdm() {
      this.$options.EventHelper.triggerButtonClicked({
        [description]: 'Navigates user to the MDM page',
        [displayLogic]: 'Visible if user has not configured MDM',
        [location]: eventLocations.immersiveOnboarding,
        [section]: 'Mac - Install via MDM Enrollment Profile',
        [text]: 'Configure MDM',
        [toUrl]: mdmLink.href,
      });
      this.$options.Util.redirect(mdmLink.href);
    },

    downloadProfile() {
      this.$options.EventHelper.triggerButtonClicked({
        [description]: 'Downloads the MDM Enrollment Profile',
        [displayLogic]: 'Visible if user has configured MDM',
        [location]: eventLocations.immersiveOnboarding,
        [section]: 'Mac - Install via MDM Enrollment Profile',
        [text]: 'Download MDM Enrollment Profile',
      });
      this.handleDownloadProfile();
    },

    async handleMounted() {
      this.isLoading = true;

      try {
        await this.fetchMdmSettings();

        if (this.getDmId) {
          this.setEnrollmentProfiles();
          this.retrieveEnrollmentProfileDownloadUrl();
        }
        this.isLoading = false;
      } catch {
        this.isLoading = false;
      }
    },

    primaryAction() {
      if (this.isApnsPushTopicEmpty) {
        this.configureMdm();
      } else {
        this.handleNext();
      }
    },
  },
};
</script>

<style scoped module>
@import './css/guided-setup.css';

.downloadBtn {
  margin: var(--jc-spacer-large) 0;
}
</style>
